// Stores the most recent D1 bookmark returned by the API in
// sessionStorage so subsequent reads in the same browser tab observe
// read-your-writes consistency across requests. Wire format is opaque
// to the client — the server encodes `p=<primary>;o=<org>` and the
// client just stashes and replays it via the X-D1-Bookmark header.
//
// Per-tab on purpose: sessionStorage is already scoped per-origin and
// per-tab, so a single fixed key is sufficient — two tabs against
// different APIs never share storage in the first place.
//
// Native (RN iOS/Android) has no sessionStorage and is single-tab per
// process anyway, so a module-level in-memory fallback is enough to
// preserve read-your-writes for the lifetime of the app process.

const STORAGE_KEY = 'd1-bookmark';

let inMemoryBookmark: string | null = null;

const hasSessionStorage = (): boolean => {
	try {
		return typeof sessionStorage !== 'undefined' && sessionStorage !== null;
	} catch {
		// Accessing sessionStorage can throw in some embedded contexts
		// (sandboxed iframes, certain privacy modes). Treat as missing.
		return false;
	}
};

export const getStoredBookmark = (): string | null => {
	if (hasSessionStorage()) {
		try {
			return sessionStorage.getItem(STORAGE_KEY);
		} catch {
			// fall through to in-memory
		}
	}
	return inMemoryBookmark;
};

export const setStoredBookmark = (value: string | null): void => {
	if (!value) {
		clearStoredBookmark();
		return;
	}
	if (hasSessionStorage()) {
		try {
			sessionStorage.setItem(STORAGE_KEY, value);
			return;
		} catch {
			// fall through to in-memory
		}
	}
	inMemoryBookmark = value;
};

export const clearStoredBookmark = (): void => {
	if (hasSessionStorage()) {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			// ignore
		}
	}
	inMemoryBookmark = null;
};
